/**
 * School Data Detection Utilities
 * 
 * Provides functions to detect what type of data is available for a school/location
 * and determine appropriate display strategies.
 */

/**
 * Lightweight drop-a-pin check for school list items (no enrollment data needed).
 * Prefers the backend-provided `is_drop_a_pin` flag (no NCES ID AND no enrollment);
 * falls back to "no NCES ID" when the flag is absent (e.g. stale cached lists).
 * @param {Object} school - School list item
 * @returns {boolean}
 */
export const isDropAPinSchool = (school) => {
  if (!school) return false;
  if (typeof school.is_drop_a_pin === 'boolean') return school.is_drop_a_pin;
  return !school.ncessch;
};

/**
 * Schools with fewer years than this get the hybrid view (enrollment history +
 * planning worksheet) instead of the standard projections dashboard.
 */
export const MIN_YEARS_FOR_PROJECTIONS = 5;

/**
 * Count school years that have at least one grade with enrollment > 0.
 * @param {Object} enrollmentData - `enrollment_data` from the enrollment-management endpoint,
 *   shaped `{ [schoolYear]: { [grade]: { total } } }`
 * @returns {number}
 */
export const countEnrollmentYears = (enrollmentData) => {
  if (!enrollmentData || typeof enrollmentData !== 'object') return 0;
  return Object.values(enrollmentData).filter((grades) =>
    grades && Object.values(grades).some((g) => Number(g?.total) > 0)
  ).length;
};

/**
 * Detect the type and availability of data for a school/location
 * @param {Object} school - School object from the school store
 * @param {Array} schoolsEnrollmentData - Array of enrollment data records
 * @returns {Object} Data availability information
 */
export const detectSchoolDataType = (school, schoolsEnrollmentData = []) => {
  if (!school) {
    return {
      hasEnrollmentData: false,
      hasESRIData: false,
      hasProjections: false,
      dataType: 'unknown',
      isDropAPinLocation: false,
      isStandardSchool: false
    };
  }

  // Find enrollment data for this school
  const enrollmentData = schoolsEnrollmentData?.find(d => d.uuid === school?.uuid);
  
  // Check if enrollment data exists - use backend flag if available, otherwise check grade data
  // The backend flag indicates whether ANY enrollment records exist, regardless of grade filtering
  // This prevents schools from being incorrectly classified as drop-a-pins when selected grades
  // don't match the school's offered grades
  const hasValidEnrollmentData = !!(
    enrollmentData?.has_enrollment_data || 
    (enrollmentData?.enrollment_by_grade?.current && 
     Object.keys(enrollmentData.enrollment_by_grade.current).length > 0)
  );

  // Check for ESRI data availability (usually indicated by has_esri_data flag or presence of location)
  const hasESRIData = !!(school.has_esri_data || school.latitude || school.longitude);

  // Check for projections (may come from school metadata or separate flag)
  const hasProjections = !!(school.has_projections);

  // Determine if this is a "Drop a Pin" location
  // These are locations without NCES IDs that were manually created for geographic analysis only
  // Drop-a-pins are identified by: no NCES ID + no enrollment data
  // They may have grades offered (for market analysis), but no actual enrollment numbers
  // Schools created via non-NCES processor will have enrollment data, so they won't be classified as drop-a-pins
  // IMPORTANT: Schools with NCES IDs should NEVER be classified as drop-a-pins, even if grade filtering
  // results in empty enrollment_by_grade objects
  const isDropAPinLocation = !school.ncessch && !hasValidEnrollmentData;

  // Determine if this is a standard school with full data
  const isStandardSchool = !!(school.ncessch && hasValidEnrollmentData);

  // Determine overall data type
  let dataType = 'unknown';
  if (isStandardSchool) {
    dataType = 'full_school';
  } else if (isDropAPinLocation && hasESRIData) {
    dataType = 'location_only';
  } else if (!hasValidEnrollmentData && hasESRIData) {
    dataType = 'location_with_demographics';
  } else if (hasValidEnrollmentData && !school.ncessch) {
    dataType = 'custom_school';
  }

  return {
    hasEnrollmentData: hasValidEnrollmentData,
    hasESRIData,
    hasProjections,
    dataType,
    isDropAPinLocation,
    isStandardSchool,
    isCustomLocation: !school.ncessch,
    enrollmentDataRecord: enrollmentData
  };
};

/**
 * Get default grade range for locations without enrollment data
 * @returns {Array} Array of grade strings representing K-12
 */
export const getDefaultGradeRange = () => {
  return ['Kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
};

/**
 * Check if a school/location should show enrollment-based analysis
 * @param {Object} school - School object
 * @param {Array} schoolsEnrollmentData - Enrollment data array
 * @returns {Boolean} True if enrollment analysis should be shown
 */
export const shouldShowEnrollmentAnalysis = (school, schoolsEnrollmentData) => {
  const { hasEnrollmentData } = detectSchoolDataType(school, schoolsEnrollmentData);
  return hasEnrollmentData;
};

/**
 * Check if a school/location should show location-based analysis
 * @param {Object} school - School object
 * @param {Array} schoolsEnrollmentData - Enrollment data array
 * @returns {Boolean} True if location-based analysis should be shown
 */
export const shouldShowLocationAnalysis = (school, schoolsEnrollmentData) => {
  const { isDropAPinLocation, hasESRIData } = detectSchoolDataType(school, schoolsEnrollmentData);
  return isDropAPinLocation && hasESRIData;
};

/**
 * Get appropriate grade range for analysis
 * @param {Object} school - School object
 * @param {Array} schoolsEnrollmentData - Enrollment data array
 * @param {Array} selectedGrades - Currently selected grades (optional)
 * @returns {Array} Array of grades to use for analysis
 */
export const getAnalysisGradeRange = (school, schoolsEnrollmentData, selectedGrades = null) => {
  const { hasEnrollmentData, enrollmentDataRecord } = detectSchoolDataType(school, schoolsEnrollmentData);
  
  if (hasEnrollmentData && selectedGrades) {
    return selectedGrades;
  }
  
  if (hasEnrollmentData && enrollmentDataRecord?.grade_range) {
    // Parse grade range from enrollment data
    const grades = enrollmentDataRecord.grade_range.split(', ').filter(Boolean);
    return grades.length > 0 ? grades : getDefaultGradeRange();
  }
  
  // For locations without enrollment data, use K-12
  return getDefaultGradeRange();
};

/**
 * Determine what type of market share calculation to use
 * @param {Object} school - School object
 * @param {Array} schoolsEnrollmentData - Enrollment data array
 * @returns {String} 'enrollment' or 'population'
 */
export const getMarketShareCalculationType = (school, schoolsEnrollmentData) => {
  const { hasEnrollmentData } = detectSchoolDataType(school, schoolsEnrollmentData);
  return hasEnrollmentData ? 'enrollment' : 'population';
};

/**
 * Get user-friendly description of location/school type
 * @param {Object} school - School object
 * @param {Array} schoolsEnrollmentData - Enrollment data array
 * @returns {String} Human readable description
 */
export const getLocationTypeDescription = (school, schoolsEnrollmentData) => {
  const { 
    isDropAPinLocation, 
    isStandardSchool, 
    hasEnrollmentData 
  } = detectSchoolDataType(school, schoolsEnrollmentData);

  if (isStandardSchool) {
    return 'School with enrollment data';
  } else if (isDropAPinLocation) {
    return 'Geographic location for market analysis';
  } else if (!hasEnrollmentData) {
    return 'Location with limited enrollment data';
  } else {
    return 'Custom location with enrollment data';
  }
};
